import { Spinner, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../shared/hooks/useApi";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import OfficeBranchModal from "./components/OfficeBranchModal";
import OfficeBranchTable from "./components/OfficeBranchTable";
import {
  OfficeBranch,
  OfficeBranchCreateParams,
  OfficeBranchEditParams,
} from "./office-branch.types";
import { OfficeBranchId, OfficeId } from "../../shared/schema/schema";

const OfficeBranchSettings = () => {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast({ duration: 3000, position: "top-right" });
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [editingBranch, setEditingBranch] = React.useState<OfficeBranch | null>(null);
  const [modalOfficeId, setModalOfficeId] = React.useState<OfficeId | null>(null);

  const branchesQuery = useQuery(queries.officeBranch.list());
  const officesQuery = useQuery(queries.office.list());

  const selectedOfficeId = editingBranch !== null ? editingBranch.officeId : modalOfficeId;

  const availableCountiesQuery = useQuery({
    ...queries.officeBranch.availableCounties(selectedOfficeId!),
    enabled: isOpen && selectedOfficeId !== null,
  });

  const branches = React.useMemo(() => branchesQuery.data?.list ?? [], [branchesQuery.data]);
  const offices = React.useMemo(() => officesQuery.data?.offices ?? [], [officesQuery.data]);

  const assignedCountiesInOffice = React.useMemo(() => {
    if (selectedOfficeId === null) return [];
    return branches
      .filter((b) => b.officeId === selectedOfficeId && b.id !== editingBranch?.id)
      .flatMap((b) => b.counties.map((c) => ({ countyName: c.countyName, state: c.state })));
  }, [branches, editingBranch, selectedOfficeId]);

  const handleAdd = (officeId?: OfficeId) => {
    setEditingBranch(null);
    setModalOfficeId(officeId ?? null);
    onOpen();
  };

  const handleEdit = (branch: OfficeBranch) => {
    setEditingBranch(branch);
    setModalOfficeId(null);
    onOpen();
  };

  const handleClose = () => {
    onClose();
    setEditingBranch(null);
    setModalOfficeId(null);
  };

  const createMutation = useMutation({
    mutationFn: (args: { officeId: OfficeId; params: OfficeBranchCreateParams }) =>
      api.post("./offices/:officeId/office_branches", {
        body: args.params,
        path: { officeId: args.officeId },
      }),
    onError: (error: unknown) => {
      toast({
        description: formatErrorResponse(error),
        status: "error",
        title: "Failed to create branch",
      });
    },
    onSuccess: () => {
      toast({ status: "success", title: "Branch created" });
      queryClient.invalidateQueries({ queryKey: ["office-branches"] });
      handleClose();
    },
  });

  const editMutation = useMutation({
    mutationFn: (args: {
      branchId: OfficeBranchId;
      officeId: OfficeId;
      params: OfficeBranchEditParams;
    }) =>
      api.put("./offices/:officeId/office_branches/:officeBranchId", {
        body: args.params,
        path: { officeId: args.officeId, officeBranchId: args.branchId },
      }),
    onError: (error: unknown) => {
      toast({
        description: formatErrorResponse(error),
        status: "error",
        title: "Failed to update branch",
      });
    },
    onSuccess: () => {
      toast({ status: "success", title: "Branch updated" });
      queryClient.invalidateQueries({ queryKey: ["office-branches"] });
      handleClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (args: { branchId: OfficeBranchId; officeId: OfficeId }) =>
      api.delete("./offices/:officeId/office_branches/:officeBranchId", {
        path: { officeId: args.officeId, officeBranchId: args.branchId },
      }),
    onError: (error: unknown) => {
      toast({
        description: formatErrorResponse(error),
        status: "error",
        title: "Failed to delete branch",
      });
    },
    onSuccess: () => {
      toast({ status: "success", title: "Branch deleted" });
      queryClient.invalidateQueries({ queryKey: ["office-branches"] });
    },
  });

  const handleDelete = (branch: OfficeBranch) => {
    const confirmed = window.confirm(`Delete branch "${branch.name}"?`);
    if (!confirmed) return;
    deleteMutation.mutate({ branchId: branch.id, officeId: branch.officeId });
  };

  const handleCreate = (officeId: OfficeId, params: OfficeBranchCreateParams) => {
    createMutation.mutate({ officeId, params });
  };

  const handleEditSubmit = (
    officeId: OfficeId,
    branchId: OfficeBranchId,
    params: OfficeBranchEditParams
  ) => {
    editMutation.mutate({ branchId, officeId, params });
  };

  const isLoading = branchesQuery.isLoading || officesQuery.isLoading;
  const error = branchesQuery.error ?? officesQuery.error;

  if (isLoading) {
    return <Spinner />;
  }

  if (error !== null && error !== undefined) {
    return <Text color="red.500">{formatErrorResponse(error)}</Text>;
  }

  const isSubmitting = createMutation.isPending || editMutation.isPending;

  return (
    <>
      <OfficeBranchTable
        branches={branches}
        offices={offices}
        onAdd={handleAdd}
        onDelete={handleDelete}
        onEdit={handleEdit}
      />
      <OfficeBranchModal
        assignedCountiesInOffice={assignedCountiesInOffice}
        availableCounties={availableCountiesQuery.data?.counties ?? []}
        branch={editingBranch}
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        offices={offices}
        onClose={handleClose}
        onCreate={handleCreate}
        onEdit={handleEditSubmit}
      />
    </>
  );
};

export default OfficeBranchSettings;
